'use client'

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { type FC, isValidElement, type ReactNode, useState } from 'react'
import Button, { type ButtonProps } from './Button'
import { Ellipsis } from 'lucide-react'
import Tooltip from './Tooltip'
import Icon, { type IconProps } from './Icon'
import { cx, cva } from 'class-variance-authority'

export interface DropdownMenuItem {
    label: string | ReactNode
    isDisabled?: boolean
    tooltip?: string
    color?: 'primary' | 'negative'
    action?: (...args: any) => Promise<any> | any
    icon?: IconProps['icon']
}

export interface DropdownMenuProps {
    'data-testid'?: string
    modal?: boolean
    trigger?: ReactNode
    items: DropdownMenuItem[]
    open?: boolean
    intent?: ButtonProps['intent']
    align?: DropdownMenuPrimitive.DropdownMenuContentProps['align']
    side?: DropdownMenuPrimitive.DropdownMenuContentProps['side']
    sideOffset?: DropdownMenuPrimitive.DropdownMenuContentProps['sideOffset']
    onOpenChange?: (open: boolean) => void
}

const triggerButtonVariants = cva('outline-hidden ring-0', {
    variants: {
        intent: {
            transparent: [
                'hover:bg-backgroundPrimary',
                'hover:ring-1',
                'hover:ring-borderPrimary',
                'data-[state=open]:bg-backgroundPrimary',
                'data-[state=open]:ring-1',
                'data-[state=open]:ring-borderPrimary'
            ],
            primary: [],
            secondary: [],
            negative: [],
            ghostPrimary: []
        }
    },
    defaultVariants: {
        intent: undefined
    }
})

const itemTextVariants = cva('', {
    variants: {
        state: {
            disabled: 'text-textDisabled',
            negative: 'text-textNegative',
            default: 'text-textPrimary'
        }
    },
    defaultVariants: {
        state: 'default'
    }
})

const itemBackgroundVariants = cva('', {
    variants: {
        state: {
            disabled: 'bg-backgroundDisabled',
            negative: 'bg-backgroundNegativeLight',
            default: 'bg-backgroundPrimaryHover'
        }
    },
    defaultVariants: {
        state: 'default'
    }
})

const DropdownMenu: FC<DropdownMenuProps> = ({
    modal,
    trigger,
    items,
    align = 'end',
    side = 'bottom',
    sideOffset = 4,
    open,
    intent,
    'data-testid': dataTestId,
    onOpenChange
}) => {
    const [backgroundPosition, setBackgroundPosition] = useState('0px')
    const [isBackgroundVisible, setIsBackgroundVisible] = useState(false)
    const [backgroundState, setBackgroundState] = useState<'default' | 'disabled' | 'negative'>('default')

    const onTrigger = (item: DropdownMenuItem) => async () => {
        if (item.action) {
            await item.action()
        }
    }

    const handleItemHover = (index: number, item: DropdownMenuItem) => {
        setBackgroundPosition(`${index * 32}px`)

        if (item.isDisabled) {
            setBackgroundState('disabled')
        } else if (item.color === 'negative') {
            setBackgroundState('negative')
        } else {
            setBackgroundState('default')
        }

        setIsBackgroundVisible(true)
    }

    const handleMenuLeave = () => {
        setIsBackgroundVisible(false)
    }

    const renderItemContent = (item: DropdownMenuItem) => (
        <div className="flex items-center gap-2 relative z-10">
            {item.icon && (
                <span className="flex items-center justify-center">
                    <Icon icon={item.icon} size="2" color={item.color === 'negative' ? 'negative' : 'primary'} />
                </span>
            )}
            <span>{item.label}</span>
        </div>
    )

    const getItemState = (item: DropdownMenuItem): 'disabled' | 'negative' | 'default' => {
        if (item.isDisabled) return 'disabled'
        if (item.color === 'negative') return 'negative'
        return 'default'
    }

    return (
        <DropdownMenuPrimitive.Root modal={modal} open={open} onOpenChange={onOpenChange}>
            <DropdownMenuPrimitive.Trigger asChild>
                {isValidElement(trigger) ? (
                    trigger
                ) : (
                    <Button
                        data-testid={dataTestId ?? '__dropdown-menu-trigger__'}
                        intent={intent ?? 'secondary'}
                        size="square"
                        className={triggerButtonVariants({
                            intent: intent === 'transparent' ? 'transparent' : undefined
                        })}
                    >
                        <Icon strokeWidth={2} icon={Ellipsis} color="primary" />
                    </Button>
                )}
            </DropdownMenuPrimitive.Trigger>
            <DropdownMenuPrimitive.Portal>
                <DropdownMenuPrimitive.Content
                    align={align}
                    side={side}
                    sideOffset={sideOffset}
                    data-testid="__dropdown-menu-content__"
                    className="min-w-[160px] bg-backgroundPrimary border border-borderDisabled rounded-md shadow-md animate-fade-in p-1 space-y-0.5 relative"
                    onClick={(e) => e.stopPropagation()}
                    onEscapeKeyDown={(e) => {
                        e.preventDefault()
                        onOpenChange?.(false)
                    }}
                    onPointerLeave={handleMenuLeave}
                >
                    <div className="relative">
                        <div
                            className={cx(
                                'absolute rounded-xs pointer-events-none transition-all duration-200 ease-out',
                                itemBackgroundVariants({ state: backgroundState })
                            )}
                            style={{
                                top: backgroundPosition,
                                left: '0',
                                width: '100%',
                                height: '32px',
                                opacity: isBackgroundVisible ? 1 : 0
                            }}
                        />

                        {items.map((item, index) => (
                            <DropdownMenuPrimitive.Item
                                data-testid={`__dropdown-menu-item-${index}__`}
                                key={typeof item.label === 'string' ? item.label : index}
                                onSelect={onTrigger(item)}
                                disabled={item.isDisabled}
                                className={cx(
                                    'relative flex items-center p-2 text-sm rounded-xs h-8 no-underline border-none outline-hidden',
                                    itemTextVariants({ state: getItemState(item) }),
                                    item.isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                                )}
                                onPointerEnter={() => handleItemHover(index, item)}
                            >
                                {item.tooltip ? (
                                    <Tooltip trigger={renderItemContent(item)} content={item.tooltip} />
                                ) : (
                                    renderItemContent(item)
                                )}
                            </DropdownMenuPrimitive.Item>
                        ))}
                    </div>
                </DropdownMenuPrimitive.Content>
            </DropdownMenuPrimitive.Portal>
        </DropdownMenuPrimitive.Root>
    )
}

export default DropdownMenu
